import { Button, Stack } from "@mui/material";
import { useBanksEnvironment } from "Api/Queries/Banks/useBanksEnvironments";
import { BankButton } from "Components/Shared/BanksList/BankButton/BankButton";
import { BankButtonSkeleton } from "Components/Shared/BanksList/BankButton/Components/BankButtonSkeleton";
import type { ActionType } from "Components/Shared/BanksList/BankButton/Components/IconSection";
import type { BankEnvironment } from "Infrastructure/Api/Api";
import { compareBanksByRating } from "Hooks/useSortedBanks";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import { isSandbox } from "Utils/BankUtils";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { ErrorText } from "Components/Shared/Errors/ErrorText";

const EXPANDABLE_LIMIT = 3;

type BanksListProps = {
  onSelect: (env: BankEnvironment) => void;
  actionType?: ActionType;
  pendingEnvironment?: BankEnvironment | null;
  bankFilter: "enabledOnly" | "disabledOnly" | "all";
  isExpandable?: boolean;
};

export const BanksList = ({
  onSelect,
  actionType,
  pendingEnvironment,
  bankFilter = "all",
  isExpandable = false,
}: BanksListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data, isLoading } = useBanksEnvironment();

  const { t, exists } = useResource();
  const isMerchant = useIsCurrentUserMerchant();

  if (isLoading) {
    return <BankButtonSkeleton />;
  }

  if (!data) {
    return <ErrorText>{t(Resources.Errors.DataLoadingError)}</ErrorText>;
  }

  const banks = data.items.filter(({ isVisible, isEnabled }) => {
    if (!isVisible) {
      return false;
    }
    if (bankFilter === "enabledOnly") {
      return isEnabled;
    }
    if (bankFilter === "disabledOnly") {
      return !isEnabled;
    }
    return true;
  });

  const sortedBanks = banks.sort((a, b) =>
    compareBanksByRating(a.bank, b.bank, t, exists, isMerchant),
  );

  const hasBanksToCollapse = sortedBanks.length > EXPANDABLE_LIMIT;
  const isCollapsed = isExpandable && !isExpanded;
  const visibleBanks = isCollapsed
    ? sortedBanks.slice(0, EXPANDABLE_LIMIT)
    : sortedBanks;

  const toggleIsExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <Stack gap={4}>
      {visibleBanks.map(({ bank, name, isEnabled, bankEnvironment }) => (
        <BankButton
          key={`${bank}-${bankEnvironment}`}
          bank={bank}
          isSandbox={isSandbox(name)}
          onClick={() => onSelect(bankEnvironment)}
          isLoading={pendingEnvironment === bankEnvironment}
          isDisabled={!isEnabled}
          actionType={actionType}
        />
      ))}
      {isExpandable && hasBanksToCollapse && (
        <Button variant="outlined" onClick={toggleIsExpanded} fullWidth>
          {isExpanded
            ? t(Resources.Components.BanksList.HideAll)
            : t(Resources.Components.BanksList.ShowAll, {
                count: banks.length - EXPANDABLE_LIMIT,
              })}
        </Button>
      )}
    </Stack>
  );
};
